// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for modifying existing asset ownership events.
 * Provides functionality to update the details of previously recorded
 * ownership transfers, including dates, prices, and customer information.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { assetOwnershipEventSchema } from '@/asset/api/types';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Schema for validating the API response when updating ownership events.
 * Transforms the response to provide a more intuitive structure with
 * the event data separated from metadata.
 */
const responseSchema = z
  .object({ data: assetOwnershipEventSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, event: data }));

/**
 * Parameters for updating an existing ownership event.
 * All fields are optional to allow partial updates of event information.
 *
 * @typedef {Object} AssetOwnershipParameters
 * @property {string} [customerId] - Updated customer identifier
 * @property {Currency | null} [salePrice] - Updated sale price or null to clear
 * @property {DateTime} [date] - Updated date when the ownership transfer occurred
 */
export interface AssetOwnershipParameters {
  customerId?: string;
  salePrice?: Currency | null;
  date?: DateTime;
}

/**
 * Updates the details of a specific ownership event for an asset.
 * This function handles the API call to modify existing ownership records
 * and validates the response data using Zod schemas. It supports partial
 * updates, allowing modification of specific fields while leaving others unchanged.
 *
 * @param assetKey - Unique identifier of the asset associated with the event
 * @param eventKey - Unique identifier of the ownership event to update
 * @param parameters - Object containing the fields to update
 * @returns Promise resolving to the updated event data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function updateAssetOwnershipEvent(
  assetKey: string,
  eventKey: string,
  parameters: AssetOwnershipParameters
) {
  const path = `/v1/asset/${assetKey}/ownership/${eventKey}`;
  const response = await api.patch(path, {
    customerId: parameters.customerId,
    salePrice: serializeOptionalCurrencyForAPI(parameters.salePrice),
    date: serializeDateForAPI(parameters.date),
  });

  return parseResponse(responseSchema, response.data, {
    schemaName: 'UpdateAssetOwnershipEvent',
    endpoint: path,
  });
}
